// client/src/pages/AccountsPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Row, Col, Button, Table, Alert, Spinner, Modal, Badge, Form, Card } from 'react-bootstrap';
import AddAccountModal from '../components/AddAccountModal';
import { useAuth } from '../context/AuthContext';
import 'bootstrap/dist/css/bootstrap.min.css';
import { getAuthHeaders } from '../utils/authHelpers';

const API_ROOT_URL = process.env.REACT_APP_API_ROOT_URL;
const ACCOUNTS_API_URL = `${API_ROOT_URL}/accounts`;

function AccountsPage() {
  const { user, logout } = useAuth();
  
  const [allAccounts, setAllAccounts] = useState([]);
  const [displayedAccounts, setDisplayedAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [pageMessage, setPageMessage] = useState('');
  const [pageMessageType, setPageMessageType] = useState('');
  const [loadingAction, setLoadingAction] = useState(false);
  const [dealers, setDealers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterRole, setFilterRole] = useState('All');
  const [managerSecret, setManagerSecret] = useState('');
  const [showManagerSecret, setShowManagerSecret] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [accountToEdit, setAccountToEdit] = useState(null);
  const [newUsername, setNewUsername] = useState('');

  const allAccountStatuses = useMemo(() => ['All', 'Registered', 'Available'], []);
  const allAccountRoles = useMemo(() => ['All', 'Admin', 'PreProduction', 'Manufacturing', 'Shipping', 'Dealer', 'Employee', 'Supervisor'], []);

  const dealerMap = useMemo(() => {
    return dealers.reduce((map, dealer) => {
      map[dealer.dealerId] = dealer.dealerName;
      return map;
    }, {});
  }, [dealers]);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    setError('');
    setPageMessage('');
    setPageMessageType('');
    try {
      const response = await fetch(ACCOUNTS_API_URL, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setAllAccounts(data);
    } catch (err) {
      console.error('Error fetching accounts:', err);
      setError('Failed to load accounts. Please ensure the backend server is running and accessible.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDealers = useCallback(async () => {
    try {
      const response = await fetch(`${API_ROOT_URL}/dealers`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setDealers(data);
    } catch (err) {
      console.error('Error fetching dealers:', err);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
    fetchDealers();
  }, [fetchAccounts, fetchDealers]);

  useEffect(() => {
    const applyFilters = () => {
      let filtered = allAccounts;
      if (searchTerm) {
        const lowerCaseSearchTerm = searchTerm.toLowerCase();
        filtered = filtered.filter(account =>
          account.accountId.toLowerCase().includes(lowerCaseSearchTerm) ||
          (account.username && account.username.toLowerCase().includes(lowerCaseSearchTerm)) ||
          (account.associatedDealerId && (
            account.associatedDealerId.toLowerCase().includes(lowerCaseSearchTerm) ||
            (dealerMap[account.associatedDealerId] && dealerMap[account.associatedDealerId].toLowerCase().includes(lowerCaseSearchTerm))
          ))
        );
      }
      if (filterStatus !== 'All') {
        const isRegistered = filterStatus === 'Registered';
        filtered = filtered.filter(account => account.isRegistered === isRegistered);
      }
      if (filterRole !== 'All') {
        filtered = filtered.filter(account => account.role === filterRole);
      }
      setDisplayedAccounts(filtered);
    };
    applyFilters();
  }, [allAccounts, searchTerm, filterStatus, filterRole, dealerMap]);

  const handlePageMessage = useCallback((message, type) => {
    setPageMessage(message);
    setPageMessageType(type);
    if (type === 'success') {
      setTimeout(() => {
        setPageMessage('');
        setPageMessageType('');
      }, 5000);
    }
  }, []);

  const handleShowAddModal = () => {
    handlePageMessage('', '');
    setShowAddModal(true);
  };
  const handleCloseAddModal = () => setShowAddModal(false);

  const handleDeleteClick = (account) => {
    handlePageMessage('', '');
    setAccountToDelete(account);
    setShowDeleteConfirm(true);
  };

  const handleEditClick = (account) => {
    setAccountToEdit(account);
    setNewUsername(account.username || '');
    setShowEditModal(true);
  };

  const handleEditConfirm = async () => {
    if (!accountToEdit) return;
    setLoadingAction(true);
    try {
      const response = await fetch(`${ACCOUNTS_API_URL}/${accountToEdit.accountId}`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username: newUsername }),
      });
      const result = await response.json();
      if (response.ok) {
        fetchAccounts();
        handlePageMessage(result.message || 'Username updated successfully!', 'success');
      } else {
        handlePageMessage(result.message || 'Failed to update username.', 'danger');
      }
    } catch (error) {
      console.error('Error updating username:', error);
      handlePageMessage('Network error or server is unreachable while updating username.', 'danger');
    } finally {
      setLoadingAction(false);
      setShowEditModal(false);
      setAccountToEdit(null);
      setNewUsername('');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!accountToDelete) return;
    handlePageMessage('', '');
    setLoadingAction(true);
    const isCurrentUser = user && accountToDelete.isRegistered && accountToDelete.registeredUserId === user._id;
    try {
      const response = await fetch(`${ACCOUNTS_API_URL}/${accountToDelete.accountId}`, {
        method: 'DELETE',
        headers: {
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ managerSecret }),
      });
      const result = await response.json();
      if (response.ok) {
        if (isCurrentUser) logout();
        else fetchAccounts();
        handlePageMessage(result.message || 'Account and linked user deleted successfully!', 'success');
      } else {
        handlePageMessage(result.message || 'Failed to delete account.', 'danger');
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      handlePageMessage('Network error or server is unreachable while deleting account.', 'danger');
    } finally {
      setLoadingAction(false);
      setShowDeleteConfirm(false);
      setAccountToDelete(null);
      setManagerSecret('');
      setShowManagerSecret(false);
    }
  };

  const handleCloseDeleteConfirm = useCallback(() => { // Add useCallback
    setShowDeleteConfirm(false);
    setAccountToDelete(null);
    setManagerSecret(''); // Also clear the secret on cancel/close
  }, []);

  const groupedAccounts = displayedAccounts.reduce((acc, account) => {
    (acc[account.role] = acc[account.role] || []).push(account);
    return acc;
  }, {});

  const sortedRoles = ['Admin', 'PreProduction', 'Manufacturing', 'Shipping', 'Supervisor', 'Employee', 'Dealer'];

  if (loading && allAccounts.length === 0) {
    return (
      <Container className="my-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading accounts...</span>
        </Spinner>
        <p className="mt-2">Loading accounts...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="my-5">
        <Alert variant="danger">
          <Alert.Heading>Error Loading Accounts</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </Container>
    );
  }

  return (
    <Container
    fluid
    style={{
      backgroundColor: '#4595ad29',
      minHeight: '100vh',
      paddingTop: '2rem',
      paddingBottom: '2rem',
      margin: 0
    }}
  >

    <Container className="my-5 ">
      <Row className="mb-4 align-items-center">
        <Col>
          <h2 className="mb-0">Manage User Accounts</h2>
        </Col>
        <Col xs="auto">
          <Button variant="success" onClick={handleShowAddModal}>
            + Generate New Account ID
          </Button>
        </Col>
      </Row>

      {pageMessage && <Alert variant={pageMessageType}>{pageMessage}</Alert>}

      {loadingAction && (
        <div className="d-flex justify-content-center my-3">
          <Spinner animation="border" size="sm" role="status">
            <span className="visually-hidden">Processing action...</span>
          </Spinner>
        </div>
      )}

      <Row className="mb-4 justify-content-center">
        <Col md={12}>
          <Card className="shadow-sm p-3">
            <Row className="align-items-center">
              <Col md={5} className="mb-3 mb-md-0">
                <Form.Group controlId="searchAccounts">
                  <Form.Label>Search</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Search by Account ID, User Reference, or Dealer ID"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3} className="mb-3 mb-md-0">
                <Form.Label>Status</Form.Label>
                <Form.Select name="accountStatusFilter" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} disabled={loadingAction}>
                  {allAccountStatuses.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={2} className="mb-3 mb-md-0">
                <Form.Label>Role</Form.Label>
                <Form.Select name="accountRoleFilter" value={filterRole} onChange={(e) => setFilterRole(e.target.value)} disabled={loadingAction}>
                  {allAccountRoles.map(role => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={2}>
                <Button variant="outline-secondary" onClick={() => { setSearchTerm(''); setFilterStatus('All'); setFilterRole('All'); }} disabled={loadingAction}>
                  Clear Filters
                </Button>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {loading && allAccounts.length > 0 && (
        <div className="d-flex justify-content-center my-3">
          <Spinner animation="border" size="sm" role="status">
            <span className="visually-hidden">Refreshing...</span>
          </Spinner>
        </div>
      )}

      {displayedAccounts.length === 0 && !loading ? (
        <Alert variant="info" className="text-center mt-3">
          No account IDs found matching your criteria. Click "Generate New Account ID" to get started!
        </Alert>
      ) : (
        <>
          {sortedRoles.map(role => groupedAccounts[role] && (
            <div key={role} className="mb-5">
              <h3 className="mb-3 mt-4">{role} Accounts</h3>
              <div className="table-responsive">
                <Table striped bordered hover responsive className="shadow-sm rounded">
                  <thead className="table-dark">
                    <tr>
                      <th>Account ID</th>
                      <th>User (Reference)</th>
                      <th>Status</th>
                      {role === 'Dealer' && <th>Associated Dealer Name (ID)</th>}
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedAccounts[role].map(account => (
                      <tr key={account.accountId}>
                        <td>{account.accountId}</td>
                        <td>{account.username || 'N/A'}</td>
                        <td>
                          <Badge bg={account.isRegistered ? 'success' : 'warning'}>
                            {account.isRegistered ? 'Registered' : 'Available'}
                          </Badge>
                        </td>
                        {role === 'Dealer' && (
                          <td>
                            {account.associatedDealerId ? (
                              dealerMap[account.associatedDealerId]
                                ? `${dealerMap[account.associatedDealerId]} (${account.associatedDealerId})`
                                : account.associatedDealerId
                            ) : 'N/A'}
                          </td>
                        )}
                        <td>
                          <Button
                            variant="warning"
                            size="sm"
                            className="me-2"
                            onClick={() => handleEditClick(account)}
                            disabled={loadingAction}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDeleteClick(account)}
                            disabled={loadingAction}
                            title={
                              account.isRegistered && user && account.registeredUserId === user._id
                                ? "Delete your own account and log out."
                                : "Delete Account ID and linked user (if any)"
                            }
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          ))}
        </>
      )}

      <AddAccountModal
        show={showAddModal}
        handleClose={handleCloseAddModal}
        refreshAccounts={fetchAccounts}
        setSubmissionMessage={handlePageMessage}
      />

      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Username (Reference)</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group controlId="editUsername">
            <Form.Label>New Username</Form.Label>
            <Form.Control
              type="text"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              placeholder="Enter new username"
              autoFocus
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleEditConfirm} disabled={loadingAction}>
            {loadingAction ? <Spinner as="span" animation="border" size="sm" /> : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDeleteConfirm} onHide={handleCloseDeleteConfirm} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete Account ID</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete account ID <strong>{accountToDelete?.accountId}</strong> (Role: {accountToDelete?.role}) for <strong>{accountToDelete?.username}</strong>?
          {accountToDelete?.isRegistered && (
            <Alert variant="warning" className="mt-2">
              This will also delete the user account linked to this ID.
              {user && accountToDelete?.registeredUserId === user._id && (
                <strong className="d-block mt-1">You will be logged out immediately.</strong>
              )}
            </Alert>
          )}
          <p className="mt-2">This action cannot be undone.</p>

          {/* Manager Password Field */}
          <Form.Group className="mt-3" controlId="managerSecret">
            <Form.Label>Manager Password</Form.Label>
            <div className="d-flex align-items-center mt-2">
              <Form.Control
                type={showManagerSecret ? "text" : "password"}
                placeholder="Enter manager password to confirm"
                value={managerSecret}
                onChange={(e) => setManagerSecret(e.target.value)}
                autoFocus
              />
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowManagerSecret(prev => !prev)}
                style={{ marginLeft: '0.5rem' }}
              >
                {showManagerSecret ? 'Hide' : 'Show'}
              </Button>
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteConfirm}>Cancel</Button>
          <Button 
            variant="danger" 
            onClick={handleDeleteConfirm} 
            disabled={loadingAction || !managerSecret}
          >
            {loadingAction ? <Spinner as="span" animation="border" size="sm" /> : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
    </Container>
  );
}

export default AccountsPage;
